import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { setTokenRefreshedHandler, tokenStore } from '@/lib/api'
import { closeCubeSession } from '@/lib/cube'
import { ApiError } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import { useAuthStore } from '@/stores/auth-store'
import type { AuthenticatedUser, ChangePasswordRequest, LoginRequest } from '@/types/models'

interface IdentityState {
  user: AuthenticatedUser | null
  mustChangePassword: boolean
}

/**
 * Backend'de `must_change_password` açık bir kullanıcının `/auth/me` dahil neredeyse
 * her ucu `password_change_guard` tarafından 403 `password_change_required` ile
 * kapatılır (bkz. `backend/app/modules/auth/deps.py`) — bu durumda kimlik bilgisi hiç
 * gelmez, yalnızca geçerli bir oturum olduğu bilinir; bu da burada `IdentityState`'e
 * indirgenir.
 */
async function fetchIdentity(options?: { skipAuthRefresh?: boolean }): Promise<IdentityState> {
  try {
    const user = await services.auth.me(options)
    return { user, mustChangePassword: Boolean(user.must_change_password) }
  } catch (error) {
    if (error instanceof ApiError && error.code === 'password_change_required') {
      return { user: null, mustChangePassword: true }
    }
    throw error
  }
}

/**
 * Açılışta çalışır: access token bellekte tutulduğu için sayfa yenilendiğinde kaybolur
 * (AGENTS.md §4). `refresh` httpOnly cookie üzerinden yeni bir access token ister;
 * başarısızsa `/auth/me` bir kez token'sız denenir: sunucu PDF'i (Gotenberg) kimliği
 * `Authorization` header'ıyla kendisi ekler, cookie'si yoktur. Normal tarayıcıda bu istek 401
 * döner ve oturum yok sayılır; SPA header'la gelen kimlikte kendi Authorization'ını eklemez.
 */
async function bootstrapIdentity(): Promise<IdentityState | null> {
  if (!tokenStore.get()) {
    const refreshed = await services.auth.refresh()
    if (!refreshed) {
      try {
        return await fetchIdentity({ skipAuthRefresh: true })
      } catch {
        return null
      }
    }
  }
  try {
    return await fetchIdentity()
  } catch {
    tokenStore.clear()
    return null
  }
}

/**
 * Arka planda token yenilenince kimliği yeniden çeker: grup/izin değişikliği sayfa yenilenmeden
 * en geç bir token ömrü (~15 dk) içinde ekrana yansır. Hata mevcut kimliğe dokunmaz; oturum
 * düşmüşse bunu zaten `apiFetch`'in 401 yolu yönetir. Uygulamada bir kez bağlanır (`App`).
 */
export function useIdentityRefresh() {
  const queryClient = useQueryClient()

  useEffect(() => {
    setTokenRefreshedHandler(() => {
      fetchIdentity()
        .then((identity) => queryClient.setQueryData(queryKeys.auth.me(), identity))
        .catch(() => {})
    })
    return () => setTokenRefreshedHandler(() => {})
  }, [queryClient])
}

/**
 * Kimlik + oturum işlemleri. Kaynak TanStack Query'dir (`queryKeys.auth.me()`);
 * `useAuthStore` yalnızca aynı veriyi senkron erişim için yansıtır (bkz. store dosyası).
 */
export function useAuth() {
  const queryClient = useQueryClient()
  const setIdentity = useAuthStore((state) => state.setIdentity)

  const meQuery = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: bootstrapIdentity,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    setIdentity(meQuery.data?.user ?? null)
  }, [meQuery.data, setIdentity])

  const loginMutation = useMutation({
    mutationFn: (request: LoginRequest) => services.auth.login(request),
    onSuccess: async () => {
      const identity = await fetchIdentity()
      queryClient.setQueryData(queryKeys.auth.me(), identity)
    },
  })

  const logoutMutation = useMutation({
    mutationFn: () => services.auth.logout(),
    onSettled: () => {
      closeCubeSession()
      queryClient.setQueryData(queryKeys.auth.me(), null)
      queryClient.clear()
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: (request: ChangePasswordRequest) => services.auth.changePassword(request),
    onSuccess: async () => {
      const identity = await fetchIdentity()
      queryClient.setQueryData(queryKeys.auth.me(), identity)
    },
  })

  return {
    user: meQuery.data?.user ?? null,
    isBootstrapping: meQuery.isLoading,
    isAuthenticated: Boolean(meQuery.data),
    mustChangePassword: meQuery.data?.mustChangePassword ?? false,
    login: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    logout: logoutMutation.mutateAsync,
    changePassword: changePasswordMutation.mutateAsync,
    isChangingPassword: changePasswordMutation.isPending,
    changePasswordError: changePasswordMutation.error,
  }
}
